import { Link } from "react-router-dom";
import { ArrowRight, Factory, HeartHandshake, ShieldCheck, Wrench } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { brand, credo, partner, team, values } from "@kanata/shared";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/sections/PageHero";
import { SectionHeading } from "@/components/sections/SectionHeading";
import { Button } from "@/components/ui/button";

const valueIcons: LucideIcon[] = [HeartHandshake, ShieldCheck, Factory, Wrench];

export default function About() {
  return (
    <>
      <Seo
        title="Tentang Kanata Indonesia"
        description="Kanata membuat standing desk ergonomis di Indonesia. Ini yang kami pegang dan siapa yang mengerjakannya."
        path="/tentang"
      />

      <PageHero
        eyebrow="Tentang Kanata"
        title="Kami bikin mejanya sendiri."
        lead={`${brand.origin}, dari rangka sampai tabletop. Itu yang bikin kami bisa tanggung jawab kalau ada yang perlu diperbaiki.`}
      />

      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand">
            {credo.heading}
          </p>
          <p className="mt-4 text-2xl font-medium leading-snug text-ink sm:text-3xl">
            {credo.intro}
          </p>
          <div className="mt-6 space-y-4">
            {credo.paragraphs.map((p) => (
              <p key={p} className="leading-relaxed text-ink/70">
                {p}
              </p>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-ink/8 bg-cream/50 py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading eyebrow="Yang kami pegang" title="Empat hal, bukan slogan." />

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((value, i) => {
              const Icon = valueIcons[i] ?? HeartHandshake;
              return (
                <div key={value.title} className="rounded-2xl border border-ink/8 bg-white p-6">
                  <div className="flex size-11 items-center justify-center rounded-xl bg-brand-tint text-brand">
                    <Icon className="size-5" />
                  </div>
                  <h3 className="mt-4 font-medium text-ink">{value.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-ink/65">{value.body}</p>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading eyebrow="Tim" title="Orang di baliknya." />

          <ul className="mt-10 grid gap-5 sm:grid-cols-3">
            {team.map((m) => (
              <li key={m.name} className="rounded-2xl border border-ink/8 bg-white p-6">
                <p className="font-medium text-ink">{m.name}</p>
                <p className="mt-0.5 text-sm text-ink/55">{m.role}</p>
              </li>
            ))}
          </ul>

          <div className="mt-10 rounded-2xl border border-ink/8 bg-cream/60 p-6 sm:p-8">
            <h3 className="font-medium text-ink">Untuk proyek kantor</h3>
            <p className="mt-2 max-w-2xl leading-relaxed text-ink/65">
              Untuk pekerjaan yang butuh konstruksi, kami kerja bareng{" "}
              <a
                href={partner.url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-brand underline underline-offset-2"
              >
                {partner.name}
              </a>{" "}
              — {partner.note}.
            </p>
            <Button asChild variant="outline" className="mt-5">
              <Link to="/kantor">
                Lihat proyek yang sudah jalan
                <ArrowRight />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
